import { useState } from 'react';
import { useNavigate, Navigate, Link } from '@tanstack/react-router'
import { isSignedIn, handleGoogleSignIn } from '../auth';
import { GoogleLogin } from '@react-oauth/google';

import { baseUrl } from '../config';

import { Button } from '../components/Button';
import { TextInput } from '../components/TextInput';
import { useTheme } from '../hooks/useTheme';
import { useSEO } from '../hooks/useSEO';

const getRequiredError = (fieldLabel, val) =>
  !val.trim() ? `${fieldLabel} is required` : '';

const getEmailFormatError = (val) =>
  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) ? 'Invalid email' : '';

const getEmailError = (email) =>
  getRequiredError('Email', email) || getEmailFormatError(email);

const getPasswordError = (password) =>
  getRequiredError('Password', password);

const getErrors = ({ email, password }) => {
  const emailError = getEmailError(email);
  const passwordError = getPasswordError(password);

  return {
    email: emailError,
    password: passwordError,
  }
}

export default function SignInForm() {
  useSEO({ title: 'Sign in – Compress GLB', robots: 'noindex, follow' });

  const navigate = useNavigate({ from: 'sign-in' });
  useTheme(); // Initialize theme

  const [values, setValues] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    
    // Real-time validation
    const newErrors = { ...errors };
    if (name === 'email') {
      newErrors.email = getEmailError(value);
    } else if (name === 'password') {
      newErrors.password = getPasswordError(value);
    }
    setErrors(newErrors);
    
    if (apiError) setApiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = getErrors(values);
    if (Object.values(validationErrors).some(Boolean)) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setApiError('');
    setIsLoading(true);

    try {
      const res = await fetch(`${baseUrl}/api/1/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: values.email, password: values.password }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        if (res.status === 403 && data.error === 'email_not_verified') {
          // Store email for verification pending page
          localStorage.setItem('pending_verification_email', values.email);
          navigate({ to: '/verification-pending' });
          return;
        }
        setApiError(data.message || 'Sign-in failed. Please try again.');
        return;
      }
      
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      navigate({ to: '/app/dashboard' });
    } catch (err) {
      setApiError(err.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setApiError('');
    setIsLoading(true);
    try {
      await handleGoogleSignIn(credentialResponse, navigate);
    } catch (err) {
      console.error("Google Sign In Failed in component:", err);
      setApiError(err.message || 'Google Sign-In failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error("Google Sign In Failed (library error)");
    setApiError('Google Sign-In failed. Please try again later.');
  };

  if (isSignedIn()) {
    return <Navigate to="/app/dashboard" />;
  }

  return (
    <div className="bg-background w-screen min-h-screen flex justify-center items-center text-foreground px-4 py-10">
      <form
        onSubmit={handleSubmit}
        className="bg-background-elevated p-8 md:p-10 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 w-full max-w-md space-y-6 border border-border/60"
        aria-label="Sign In Form"
      >
        <header className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">Welcome Back</h1>
          <p className="text-sm text-muted-foreground">Sign in to your account to continue</p>
        </header>
        
        {apiError && (
          <div 
            role="alert" 
            aria-label="sign-in error"
            className="bg-danger/10 border border-danger/20 text-danger px-4 py-3 rounded-lg text-sm"
          >
            {apiError}
          </div>
        )}
        
        <div className="space-y-4">
          <TextInput
            label="Email"
            name="email"
            type="text"
            value={values.email}
            onChange={handleChange}
            error={errors.email}
            disabled={isLoading}
            autoComplete="email"
          />
          <TextInput
            label="Password"
            name="password"
            type="password"
            value={values.password}
            onChange={handleChange}
            error={errors.password}
            disabled={isLoading}
            showPasswordToggle
            autoComplete="current-password"
          />
          
          <div className="flex justify-end pt-1">
            <Link 
              to="/forgot-password" 
              className="text-sm text-accent hover:text-accent/90 transition-colors font-medium"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        <Button type="submit" disabled={isLoading} className="w-full" size="lg">
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>

        <div className="relative flex py-4 items-center">
          <div className="flex-grow border-t border-border/60"></div>
          <span className="flex-shrink mx-4 text-sm text-muted-foreground">Or continue with</span>
          <div className="flex-grow border-t border-border/60"></div>
        </div>

        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap
          />
        </div>

        <p className="text-sm text-center text-muted-foreground pt-2">
          Don't have an account?{' '}
          <Link to="/sign-up" className="text-accent hover:text-accent/90 font-medium transition-colors">Sign up</Link>
        </p>
      </form>
    </div>
  );
}
